import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Grid, Chip, CircularProgress, Divider, Alert,
} from '@mui/material';
import { Download, TableChart, People, EventNote, AttachMoney, BeachAccess } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import AsyncStorage from '../../services/AsyncStorage';
import dayjs from 'dayjs';

const EXPORT_TYPES = [
  { key: 'employees',  label: 'Employees',  icon: <People />,      color: '#1a237e', bg: '#e8eaf6',  desc: 'All active employee records' },
  { key: 'attendance', label: 'Attendance', icon: <EventNote />,   color: '#1a237e', bg: '#e8eaf6',  desc: 'Attendance records by date range' },
  { key: 'salary',     label: 'Salary',     icon: <AttachMoney />, color: '#1a237e', bg: '#e8eaf6',  desc: 'Salary payment records' },
  { key: 'leave',      label: 'Leave',      icon: <BeachAccess />, color: '#1a237e', bg: '#e8eaf6',  desc: 'Leave applications' },
];

export default function ExcelExport() {
  const [exportType, setExportType] = useState('attendance');
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [department, setDepartment] = useState('All');
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const loadDepts = async () => {
      const data = await InvokeApi(Config.main_url + apiList.getDeptartments, { status: 'Active' });
      setDepartments(data?.department || []);
    };
    loadDepts();
  }, []);

  const handleExport = async () => {
    if (dayjs(startDate).isAfter(dayjs(endDate))) {
      setResult({ success: false, message: 'Start date cannot be after end date' });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = await AsyncStorage.getItem('token');
      const params = new URLSearchParams({
        type: exportType,
        startDate,
        endDate,
        department,
      });

      const url = `${Config.main_url}/exportExcel?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errText = await response.text();
        let errMsg = 'Export failed';
        try { errMsg = JSON.parse(errText)?.error || errMsg; } catch {}
        setResult({ success: false, message: errMsg });
        return;
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || `${exportType}_export_${startDate}_to_${endDate}.xlsx`;

      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);

      setResult({ success: true, message: `Downloaded: ${filename}` });
    } catch (err) {
      console.error(err);
      setResult({ success: false, message: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const activeType = EXPORT_TYPES.find(t => t.key === exportType);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color="#1a237e">Excel Export</Typography>
        <Typography variant="body2" color="text.secondary">Download employee data as Excel spreadsheets</Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography fontWeight={700} color="#1a237e" mb={2}>Select Export Type</Typography>

              <Grid container spacing={1.5} mb={3}>
                {EXPORT_TYPES.map(t => (
                  <Grid item xs={6} sm={3} key={t.key}>
                    <Box onClick={() => setExportType(t.key)} sx={{
                      p: 2, borderRadius: 2, cursor: 'pointer', textAlign: 'center',
                      border: '2px solid', transition: 'all 0.2s',
                      borderColor: exportType === t.key ? t.color : '#e8eaf6',
                      background: exportType === t.key ? t.bg : '#fff',
                      '&:hover': { background: t.bg, borderColor: t.color },
                    }}>
                      <Box sx={{ color: exportType === t.key ? t.color : 'text.secondary', mb: 0.5 }}>
                        {React.cloneElement(t.icon, { sx: { fontSize: 28 } })}
                      </Box>
                      <Typography fontSize={13} fontWeight={700} color={exportType === t.key ? t.color : 'text.secondary'}>
                        {t.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* <Typography fontSize={12} color="text.secondary" mb={2.5} sx={{ background: activeType?.bg, p: 1.5, borderRadius: 2 }}>
                 {activeType?.desc}
              </Typography> */}

              <Divider sx={{ mb: 2.5 }} />

              <Typography fontWeight={700} color="#1a237e" mb={2}>Filter Options</Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="date" label="Start Date"
                    value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="date" label="End Date"
                    value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField select fullWidth size="small" label="Department" value={department}
                    onChange={(e) => setDepartment(e.target.value)}>
                    <MenuItem value="All">All Departments</MenuItem>
                    {departments.map(d => <MenuItem key={d.ID} value={d.department}>{d.department}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>

              {result && (
                <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2, borderRadius: 2 }}>
                  {result.message}
                </Alert>
              )}

              <Button fullWidth variant="contained" size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                onClick={handleExport} disabled={loading}
                sx={{
                  mt: 3, py: 1.5, borderRadius: 2, fontWeight: 700, fontSize: 15,
                  background: `linear-gradient(135deg, ${activeType?.color || '#1a237e'}, ${activeType?.color || '#1565c0'}cc)`,
                  boxShadow: `0 8px 24px ${activeType?.color || '#1a237e'}40`,
                  '&:hover': { opacity: 0.9 },
                }}>
                {loading ? 'Generating Excel...' : `Download ${activeType?.label} Excel`}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, background: activeType?.bg }}>
                  {activeType && React.cloneElement(activeType.icon, { sx: { color: activeType.color, fontSize: 24 } })}
                </Box>
                <Box>
                  <Typography fontWeight={800} color="#1a237e">{activeType?.label} Export</Typography>
                  <Typography fontSize={12} color="text.secondary">{activeType?.desc}</Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Typography fontSize={13} fontWeight={700} color="#1a237e" mb={1}>Columns included:</Typography>
              {exportType === 'employees' && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {['ID', 'Name', 'Phone', 'Email', 'Department', 'Designation', 'Join Date'].map(c => (
                    <Chip key={c} label={c} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontSize: 10, fontWeight: 600 }} />
                  ))}
                </Box>
              )}
              {exportType === 'attendance' && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {['ID', 'Employee Name', 'Department', 'Date', 'Status', 'Check In', 'Check Out'].map(c => (
                    <Chip key={c} label={c} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontSize: 10, fontWeight: 600 }} />
                  ))}
                </Box>
              )}
              {exportType === 'salary' && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {['ID', 'Employee Name', 'Department', 'Amount', 'Status', 'Date', 'Receipt No'].map(c => (
                    <Chip key={c} label={c} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontSize: 10, fontWeight: 600 }} />
                  ))}
                </Box>
              )}
              {exportType === 'leave' && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {['ID', 'Employee Name', 'Department', 'Leave Type', 'Start Date', 'End Date', 'Reason', 'Status', 'Applied Date'].map(c => (
                    <Chip key={c} label={c} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontSize: 10, fontWeight: 600 }} />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
